import { useCallback, useEffect, useRef, useState } from 'react'
import { connectWebSocket } from '../services/websocket'
import { onRoomsStatus } from './roomsStatus'

/**
 * Why a resync was triggered.
 *
 * - `reconnect_gap`  — the socket was down long enough that events broadcast
 *                      during the gap were delivered to a room we had left.
 * - `visibility_gap` — the tab was hidden long enough that the browser may have
 *                      throttled or suspended the connection (mobile especially).
 * - `stall_timeout`  — still connected and still subscribed, but an active
 *                      resource has gone quiet past any plausible cadence. The
 *                      usual cause is a worker or pod that died mid-job, so the
 *                      terminal event was never emitted by anyone.
 */
export type RealtimeRepairReason =
  | 'reconnect_gap'
  | 'visibility_gap'
  | 'stall_timeout'

export interface UseRealtimeRepairOptions {
  /**
   * Whether the watched resource is currently expected to produce events, i.e.
   * non-terminal. The stall watchdog only runs while this is true — a finished
   * resource is *supposed* to be silent.
   */
  active: boolean
  /**
   * Resync this resource. Implementations should re-emit their `subscribe-*`
   * message (which makes the gateway replay its snapshot) and/or refetch over
   * HTTP. Must be safe to call repeatedly.
   */
  onRepair: (reason: RealtimeRepairReason) => void
  /**
   * Quiet period tolerated while `active` before assuming an event was lost.
   * Size this well above the resource's normal event cadence: a false stall
   * costs one redundant resync, so the default is deliberately generous.
   */
  stallTimeoutMs?: number
  /** Disconnect duration past which a reconnect implies missed events. */
  reconnectGapMs?: number
  /** Hidden duration past which returning to the tab implies missed events. */
  visibilityGapMs?: number
}

export interface RealtimeRepairState {
  isConnected: boolean
  /**
   * The server reported that some events cannot reach this socket. Consumers
   * should not treat silence as "nothing changed" while this is true.
   */
  isDegraded: boolean
  /**
   * Call on every event received for the watched resource. Resets the stall
   * watchdog and clears its backoff.
   */
  notifyActivity: () => void
}

const DEFAULT_STALL_TIMEOUT_MS = 45_000
const DEFAULT_RECONNECT_GAP_MS = 2_000
const DEFAULT_VISIBILITY_GAP_MS = 60_000
/** Cap on the doubling applied after each consecutive stall repair. */
const MAX_STALL_BACKOFF_MULTIPLIER = 8

/**
 * The three repair triggers that make socket-only data flow safe.
 *
 * Snapshot-on-subscribe heals a client at the moment it subscribes, which covers
 * a clean reconnect but nothing else. Two failure modes survive it: a browser
 * that suspended the tab and missed events without ever noticing a disconnect,
 * and a server-side worker that died without emitting a terminal event, leaving a
 * perfectly healthy socket waiting on something nobody will ever send. Polling
 * used to mask both. This hook replaces that safety net with a bounded number of
 * targeted resyncs instead of a continuous request stream.
 *
 * Repair is idempotent by construction: every path ends in "re-subscribe and take
 * the server's snapshot", so firing one unnecessarily is wasteful, never wrong.
 */
export function useRealtimeRepair({
  active,
  onRepair,
  stallTimeoutMs = DEFAULT_STALL_TIMEOUT_MS,
  reconnectGapMs = DEFAULT_RECONNECT_GAP_MS,
  visibilityGapMs = DEFAULT_VISIBILITY_GAP_MS,
}: UseRealtimeRepairOptions): RealtimeRepairState {
  const [isConnected, setIsConnected] = useState(false)
  const [isDegraded, setIsDegraded] = useState(false)

  // Held in a ref so changing the callback identity does not tear down and
  // restart the watchdog on every render of the consuming component.
  const onRepairRef = useRef(onRepair)
  onRepairRef.current = onRepair

  const disconnectedAtRef = useRef<number | null>(null)
  const hasConnectedOnceRef = useRef(false)
  const hiddenSinceRef = useRef<number | null>(null)
  const stallTimerRef = useRef<number | null>(null)
  const stallAttemptsRef = useRef(0)
  const lastActivityAtRef = useRef(Date.now())

  const clearStallTimer = useCallback(() => {
    if (stallTimerRef.current !== null) {
      window.clearTimeout(stallTimerRef.current)
      stallTimerRef.current = null
    }
  }, [])

  const armStallTimer = useCallback(() => {
    clearStallTimer()
    if (!active) {
      return
    }
    // Back off after each consecutive stall so a genuinely dead resource costs a
    // handful of resyncs rather than one every stallTimeoutMs forever.
    const multiplier = Math.min(
      2 ** stallAttemptsRef.current,
      MAX_STALL_BACKOFF_MULTIPLIER,
    )
    stallTimerRef.current = window.setTimeout(() => {
      stallTimerRef.current = null
      stallAttemptsRef.current += 1
      onRepairRef.current('stall_timeout')
      armStallTimer()
    }, stallTimeoutMs * multiplier)
  }, [active, clearStallTimer, stallTimeoutMs])

  const notifyActivity = useCallback(() => {
    lastActivityAtRef.current = Date.now()
    stallAttemptsRef.current = 0
    armStallTimer()
  }, [armStallTimer])

  useEffect(() => {
    if (active) {
      armStallTimer()
    } else {
      // A terminal resource is supposed to go quiet. Reset the backoff so a
      // resource that becomes active again starts from the full timeout.
      stallAttemptsRef.current = 0
      clearStallTimer()
    }
    return clearStallTimer
  }, [active, armStallTimer, clearStallTimer])

  useEffect(() => {
    const socket = connectWebSocket()

    const handleConnect = () => {
      setIsConnected(true)
      const disconnectedAt = disconnectedAtRef.current
      disconnectedAtRef.current = null
      const missedEvents =
        hasConnectedOnceRef.current &&
        disconnectedAt !== null &&
        Date.now() - disconnectedAt >= reconnectGapMs
      hasConnectedOnceRef.current = true
      if (missedEvents) {
        onRepairRef.current('reconnect_gap')
      }
      // Disconnect clears the stall timer. A blip shorter than reconnectGapMs
      // does not call onRepair, so the watchdog must be armed again here or an
      // active resource can wait forever after a brief drop.
      armStallTimer()
    }

    const handleDisconnect = () => {
      setIsConnected(false)
      disconnectedAtRef.current = Date.now()
      // Nothing can arrive while disconnected, so a stall timer would only fire a
      // repair that cannot be delivered. handleConnect covers the gap instead.
      clearStallTimer()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        hiddenSinceRef.current = Date.now()
        return
      }
      const hiddenSince = hiddenSinceRef.current
      hiddenSinceRef.current = null
      if (hiddenSince !== null && Date.now() - hiddenSince >= visibilityGapMs) {
        onRepairRef.current('visibility_gap')
      }
      // Timers are unreliable in a backgrounded tab; restart from now.
      armStallTimer()
    }

    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    const offRoomsStatus = onRoomsStatus((status) => {
      setIsDegraded(status.degraded)
    })

    if (socket.connected) {
      setIsConnected(true)
      hasConnectedOnceRef.current = true
    }

    return () => {
      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      offRoomsStatus()
    }
  }, [armStallTimer, clearStallTimer, reconnectGapMs, visibilityGapMs])

  return { isConnected, isDegraded, notifyActivity }
}
